import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import GlobalMobileSearchTabContentContainer from './GlobalMobileSearchTabContentContainer'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import TabButton from 'LuxKit/Tabs/TabButton'
import { centreElementInView } from 'lib/window/scrollUtils'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { useScreenSizeOnly } from 'hooks/useScreenSize'

interface Props {
  isOpen: boolean;
  tabs: Map<App.SearchVertical, App.GlobalSearchVerticalTabV2>;
  selectedTab?: App.GlobalSearchVerticalTabV2;
  closeSearch: () => void;
  onTabSelect: (key: App.SearchVertical) => void;
  onSearch?: () => void;
}

function GlobalMobileSearchV2(props: Props) {
  const { isOpen, closeSearch, tabs, selectedTab, onTabSelect, onSearch } = props
  const isMobileScreen = useScreenSizeOnly('mobile')
  const tabsArray = useMemo(() => [...tabs.entries()], [tabs])
  const tabsRef = useRef<Record<string, HTMLButtonElement>>({})

  const onTabClick = useCallback((searchVertical: App.SearchVertical) => {
    onTabSelect(searchVertical)
    if (searchVertical !== 'hotels') {
      setShouldShowSearch(true)
    }
  }, [onTabSelect])

  const onCloseClick = useCallback(() => {
    closeSearch()
    setShouldShowSearch(true)
  }, [closeSearch])

  const [shouldShowSearch, setShouldShowSearch] = useState(true)
  const [isSearchEnabled, setIsSearchEnabled] = useState(true)

  const selectedTabElement = selectedTab ? tabsRef.current?.[selectedTab.key] : undefined

  useEffect(() => {
    centreElementInView(selectedTabElement)
  }, [selectedTabElement])

  const title = 'Search your next escape'

  return (
    <ModalBase
      mode="drawer"
      isOpen={isMobileScreen && isOpen}
      onClose={onCloseClick}
    >
      <ModalHeader
        title={title}
        onCloseButtonClick={onCloseClick}
      />
      <ModalBody>
        <ModalContent flushListItems>
          <TabButtonsGroup overflowGutter={20}>
            {tabsArray.map(([searchVertical, { label, Icon }]) => <TabButton
              size="medium"
              key={searchVertical}
              isActive={searchVertical === selectedTab?.key}
              onClick={() => onTabClick(searchVertical)}
              ref={(node) => { if (node) tabsRef.current[searchVertical] = node }}
              format="titlecase"
              startIcon={<Icon />}
            >
              {label}
            </TabButton>)}
          </TabButtonsGroup>
          {selectedTab && <GlobalMobileSearchTabContentContainer
            tab={selectedTab}
            closeSearch={onCloseClick}
            onSearch={onSearch}
            showSearch={setShouldShowSearch}
            onSearchValidate={setIsSearchEnabled}
          />}
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          disabled: !isSearchEnabled,
          children: shouldShowSearch ? 'Search' : '',
          type: selectedTab?.formId ? 'submit' : 'button',
          form: selectedTab?.formId,
          onClick: selectedTab?.formId ? undefined : onSearch,
        }}
      />
    </ModalBase>
  )
}

export default GlobalMobileSearchV2
